import { ActionIcon, Box, Flex, Group, Menu, Pagination, Switch, Table, Text } from '@mantine/core';
import { IconDotsVertical } from '@tabler/icons-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { handleChangeLocaleTime } from '../../../../../../../shared/utils/time';

import type { IEquipmentModel } from '../../../../../../../shared/apis/ai-agent/model/types';
import type { ApiResponse } from '../../../../../../../shared/types/response/types';
import usePutMgptUse from '../hooks/usePutMgptUse';
import { buildSearchString } from '../../../utils/buildSearchString';
import HoverCardText from '../../../../../../ui/hoverCardText';

interface IModelListTableProps {
  data?: ApiResponse<IEquipmentModel>;
  page: number;
  size: number;
  onPageChange: (page: number) => void;
}

export default function ModelListTable(props: IModelListTableProps) {
  const { data, page, size, onPageChange } = props;
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [searchParams] = useSearchParams();
  const companyName = searchParams.get('companyName') || '';
  const totalCount = data?.count ?? 0;
  const totalPage = Math.ceil(totalCount / size);

  const { mutate: putMgptUse } = usePutMgptUse();

  return (
    <Box mt="lg">
      <Text fw={600} mb="sm">
        기계모델 목록
      </Text>
      <Table.ScrollContainer minWidth={1000}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>번호</Table.Th>
              <Table.Th>기계모델명(모델코드)</Table.Th>
              <Table.Th>제품군명</Table.Th>
              <Table.Th>서비스 준비 상태</Table.Th>
              <Table.Th>MachineGPT 적용 여부</Table.Th>
              <Table.Th>최종 수정 담당자</Table.Th>
              <Table.Th>최종 수정일시</Table.Th>
              <Table.Th>관리</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data && data.result.length > 0 ? (
              data.result.map((item, index) => (
                <Table.Tr
                  key={item.modelId}
                  style={(theme) => ({
                    color: item.deleteDt ? theme.colors.gray[5] : 'inherit',
                  })}
                >
                  <Table.Td>{item.modelId}</Table.Td>
                  <Table.Td ta="left" maw={200}>
                    <HoverCardText value={`${item.assignedName}(${item.model})`} />
                  </Table.Td>
                  <Table.Td ta="left" maw={200}>
                    <HoverCardText value={item.productGroup || '-'} />
                  </Table.Td>
                  <Table.Td>{item.status}</Table.Td>
                  <Table.Td>
                    <Flex justify="center">
                      <Switch
                        disabled={!!item.deleteDt}
                        checked={item.mgptUseYn === 'Y'}
                        onChange={() => {
                          putMgptUse({
                            companyId: Number(companyId),
                            data: [
                              {
                                modelId: item.modelId,
                                mgptUseYn: item.mgptUseYn === 'Y' ? 'N' : 'Y',
                              },
                            ],
                          });
                        }}
                      />
                    </Flex>
                  </Table.Td>
                  <Table.Td ta="left">{item.modifierEmail}</Table.Td>
                  <Table.Td>{handleChangeLocaleTime(item.modifiedAt)}</Table.Td>
                  <Table.Td>
                    <Menu shadow="md" withArrow width={120} position="bottom-end" offset={4} withinPortal>
                      <Menu.Target>
                        <ActionIcon variant="default">
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          onClick={() =>
                            navigate(
                              `/admin/management/aiagent/company/${companyId}/model/${item.modelId}/history${buildSearchString({ companyName: companyName, modelName: `${item.assignedName}(${item.model})` })}`,
                            )
                          }
                        >
                          이력 조회
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))
            ) : (
              <Table.Tr>
                <Table.Td colSpan={8} ta="center">
                  데이터가 없습니다.
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {totalCount > 0 && (
        <Group justify="center" mt="md">
          <Pagination total={totalPage} value={page} onChange={onPageChange} />
        </Group>
      )}
    </Box>
  );
}
